import type { ContractedBook } from '@/data/types';
import type { GameStateData } from '@/state/GameState';
import { DAGER_PER_AAR } from '@/systems/sesong';

export const ARSOPPSUMMERING_MAX_AAR = 10;

export type ArsOppsummeringTopBok = {
  isbn: string;
  tittel: string;
  salg: number;
};

export type ArsOppsummering = {
  aar: number;
  startDag: number;
  sluttDag: number;
  totalInntekt: number;
  totalUtgift: number;
  netto: number;
  utgivelser: number;
  bestselgere: number;
  brageVinster: number;
  prisVinster: number;
  topBok?: ArsOppsummeringTopBok;
  toneEvents: {
    vant: number;
    tapt: number;
  };
};

/**
 * Bygger en årsoppsummering for et avsluttet år (aar = året som nettopp ble fullført).
 *
 * sluttDag er den siste dagen i året (aar * DAGER_PER_AAR). startDag er første dag
 * (sluttDag - DAGER_PER_AAR + 1). Vi beregner tall fra `historikk` (siste 120 dager)
 * og `brageHistorikk`/`prisHistorikk` filtrert til året, samt antall internasjonale
 * avtaler inngått i året.
 */
export function beregnArsOppsummering(
  state: GameStateData,
  aar: number,
): ArsOppsummering {
  const sluttDag = aar * DAGER_PER_AAR;
  const startDag = sluttDag - DAGER_PER_AAR + 1;

  const snapshotsIAar = state.historikk.filter(
    (s) => s.dag >= startDag && s.dag <= sluttDag,
  );

  let totalInntekt = 0;
  let totalUtgift = 0;
  let utgivelserMax = 0;
  let bestselgereMax = 0;
  for (const s of snapshotsIAar) {
    totalInntekt += s.inntekt;
    totalUtgift += s.utgift;
    if (s.utgivelser > utgivelserMax) utgivelserMax = s.utgivelser;
    if (s.bestselgere > bestselgereMax) bestselgereMax = s.bestselgere;
  }

  const utgitteIAar = alleUtgitteBokerIAar(state, startDag, sluttDag);
  const utgivelser = utgitteIAar.length;
  const bestselgere = Math.max(bestselgereMax, antallBestselgereIAar(utgitteIAar));

  const brageVinster = state.brageHistorikk.filter(
    (v) => v.forlag === 'spiller',
  ).length;
  const brageVinsterIAar = countBrageVinsterIAar(state, aar);

  const prisVinsterIAar = countPrisVinsterIAar(state, aar);

  const topBok = finnTopBokFraBoker(utgitteIAar);

  return {
    aar,
    startDag,
    sluttDag,
    totalInntekt,
    totalUtgift,
    netto: totalInntekt - totalUtgift,
    utgivelser,
    bestselgere,
    brageVinster: brageVinsterIAar > 0 ? brageVinsterIAar : 0,
    prisVinster: prisVinsterIAar,
    topBok,
    toneEvents: {
      vant: 0,
      tapt: 0,
    },
  };
  void brageVinster;
}

function alleUtgitteBokerIAar(
  state: GameStateData,
  startDag: number,
  sluttDag: number,
): ContractedBook[] {
  const ut: ContractedBook[] = [];
  for (const bok of state.utgitteBoker) {
    if (bok.utgivelsesDag === null) continue;
    if (bok.utgivelsesDag < startDag || bok.utgivelsesDag > sluttDag) continue;
    ut.push(bok);
  }
  for (const bok of state.iProduksjon) {
    if (bok.status !== 'utgitt') continue;
    if (bok.utgivelsesDag === null) continue;
    if (bok.utgivelsesDag < startDag || bok.utgivelsesDag > sluttDag) continue;
    ut.push(bok);
  }
  return ut;
}

function antallBestselgereIAar(boker: ContractedBook[]): number {
  let n = 0;
  for (const b of boker) {
    if (b.salg >= 5000) n += 1;
  }
  return n;
}

function countBrageVinsterIAar(state: GameStateData, _aar: number): number {
  // brageHistorikk inneholder ikke aar-feltet direkte i vinner-objektet,
  // men vi kan se på brageStatus om det matcher
  let n = 0;
  if (
    state.brageStatus &&
    state.brageStatus.aar === _aar &&
    state.brageStatus.fase === 'vinner_annonsert'
  ) {
    for (const v of Object.values(state.brageStatus.vinnere)) {
      if (v && v.forlag === 'spiller') n += 1;
    }
  }
  return n;
}

function countPrisVinsterIAar(state: GameStateData, aar: number): number {
  let n = 0;
  for (const v of state.prisHistorikk) {
    if (v.aar !== aar) continue;
    if (v.forlag !== 'spiller') continue;
    n += 1;
  }
  return n;
}

function finnTopBokFraBoker(
  boker: ContractedBook[],
): ArsOppsummeringTopBok | undefined {
  if (boker.length === 0) return undefined;
  let beste = boker[0];
  for (const b of boker) {
    if (b.salg > beste.salg) beste = b;
  }
  if (beste.salg <= 0) return undefined;
  return { isbn: beste.isbn, tittel: beste.title, salg: beste.salg };
}

export function hydrateArsOppsummering(raw: unknown): ArsOppsummering | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<ArsOppsummering> & { toneEvents?: unknown };
  if (typeof r.aar !== 'number' || !Number.isFinite(r.aar)) return null;
  const startDag = typeof r.startDag === 'number' ? r.startDag : 0;
  const sluttDag = typeof r.sluttDag === 'number' ? r.sluttDag : 0;
  const totalInntekt = typeof r.totalInntekt === 'number' ? r.totalInntekt : 0;
  const totalUtgift = typeof r.totalUtgift === 'number' ? r.totalUtgift : 0;
  const netto =
    typeof r.netto === 'number' ? r.netto : totalInntekt - totalUtgift;
  const utgivelser = typeof r.utgivelser === 'number' ? r.utgivelser : 0;
  const bestselgere = typeof r.bestselgere === 'number' ? r.bestselgere : 0;
  const brageVinster = typeof r.brageVinster === 'number' ? r.brageVinster : 0;
  const prisVinster = typeof r.prisVinster === 'number' ? r.prisVinster : 0;
  let topBok: ArsOppsummeringTopBok | undefined;
  if (r.topBok && typeof r.topBok === 'object') {
    const tb = r.topBok as Partial<ArsOppsummeringTopBok>;
    if (typeof tb.isbn === 'string' && typeof tb.tittel === 'string') {
      topBok = {
        isbn: tb.isbn,
        tittel: tb.tittel,
        salg: typeof tb.salg === 'number' ? tb.salg : 0,
      };
    }
  }
  let toneEvents = { vant: 0, tapt: 0 };
  if (r.toneEvents && typeof r.toneEvents === 'object') {
    const te = r.toneEvents as { vant?: number; tapt?: number };
    toneEvents = {
      vant: typeof te.vant === 'number' ? te.vant : 0,
      tapt: typeof te.tapt === 'number' ? te.tapt : 0,
    };
  }
  return {
    aar: r.aar,
    startDag,
    sluttDag,
    totalInntekt,
    totalUtgift,
    netto,
    utgivelser,
    bestselgere,
    brageVinster,
    prisVinster,
    topBok,
    toneEvents,
  };
}

export function hydrateArsoppsummeringer(raw: unknown): ArsOppsummering[] {
  if (!Array.isArray(raw)) return [];
  const ut: ArsOppsummering[] = [];
  for (const r of raw) {
    const h = hydrateArsOppsummering(r);
    if (h) ut.push(h);
  }
  if (ut.length > ARSOPPSUMMERING_MAX_AAR) {
    return ut.slice(ut.length - ARSOPPSUMMERING_MAX_AAR);
  }
  return ut;
}

export function cloneArsOppsummering(o: ArsOppsummering): ArsOppsummering {
  return {
    aar: o.aar,
    startDag: o.startDag,
    sluttDag: o.sluttDag,
    totalInntekt: o.totalInntekt,
    totalUtgift: o.totalUtgift,
    netto: o.netto,
    utgivelser: o.utgivelser,
    bestselgere: o.bestselgere,
    brageVinster: o.brageVinster,
    prisVinster: o.prisVinster,
    topBok: o.topBok ? { ...o.topBok } : undefined,
    toneEvents: { ...o.toneEvents },
  };
}

